import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { ChevronDown, Plus, Check, FolderOpen, Pencil, Trash2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { NewProjectOnboarding } from './NewProjectOnboarding';
import { useSubscription } from '../../context/SubscriptionContext';
import { UpgradeGatePopup } from '../subscription/UpgradeGatePopup';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

export const ProjectSwitcher = ({ onSwitch }) => {
  const { token, getAuthHeader, activeProjectId, setActiveProjectId } = useAuth();
  const { lang } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [showBrandGate, setShowBrandGate] = useState(false);
  const sub = useSubscription();
  const [deleting, setDeleting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const fetchProjects = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/projects`, { headers: getAuthHeader() });
      setProjects(data.projects || []);

      if (!activeProjectId && data.active_project_id) {
        setActiveProjectId(data.active_project_id);
      }
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  }, [token, getAuthHeader, activeProjectId, setActiveProjectId]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const activeProject = projects.find(p => p.id === activeProjectId);

  const handleSwitch = async (projectId) => {
    if (projectId === activeProjectId) return;
    // Close menu first, then switch after Radix cleanup
    setMenuOpen(false);
    await setActiveProjectId(projectId);
    toast.success(lang === 'en' ? 'Project switched' : 'Projekt gewechselt');
    // Delay onSwitch to let Radix fully unmount the menu
    setTimeout(() => onSwitch?.(), 150);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/projects/${deleteTarget.id}`, { headers: getAuthHeader() });
      toast.success(`"${deleteTarget.name}" gelöscht`);

      // If we deleted the active project, switch to default
      if (deleteTarget.id === activeProjectId) {
        const defaultProj = projects.find(p => p.is_default);
        if (defaultProj) {
          await setActiveProjectId(defaultProj.id);
        }
      }

      setDeleteTarget(null);
      fetchProjects();
      setTimeout(() => onSwitch?.(), 150);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Löschen');
    } finally {
      setDeleting(false);
    }
  };

  const openRename = (project) => {
    setRenameTarget(project);
    setRenameValue(project.name || '');
  };

  const handleRename = async () => {
    if (!renameTarget) return;
    const newName = (renameValue || '').trim();
    if (!newName) {
      toast.error('Projektname darf nicht leer sein');
      return;
    }
    if (newName === renameTarget.name) {
      setRenameTarget(null);
      return;
    }
    setRenaming(true);
    try {
      await axios.put(
        `${API}/projects/${renameTarget.id}`,
        { name: newName },
        { headers: getAuthHeader() }
      );
      toast.success('Projekt umbenannt');
      setRenameTarget(null);
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Umbenennen fehlgeschlagen');
    } finally {
      setRenaming(false);
    }
  };

  const handleOnboardingComplete = (project, mode) => {
    setShowOnboarding(false);
    if (mode === 'onboarding') {
      sessionStorage.setItem('project_onboarding', 'true');
      // Komplett-unabhängiges Projekt: vollständiges Onboarding starten
      // (Website-Analyse + Brand DNA wird via claim-for-project an das
      // aktuell aktive (= neu erstellte) Projekt gebunden.)
      window.location.href = '/onboarding';
      return;
    }
    fetchProjects();
    setTimeout(() => onSwitch?.(), 150);
  };

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="w-full justify-between gap-2 px-3 py-2 h-auto text-left hover:bg-[#1A2B42] border border-[#1A2B42] rounded-lg"
            data-testid="project-switcher-trigger"
          >
            <div className="flex items-center gap-2 min-w-0">
              <FolderOpen className="w-4 h-4 text-[#00D9C8] shrink-0" />
              <span className="text-sm text-white truncate">
                {activeProject?.name || 'Projekt wählen'}
              </span>
            </div>
            <ChevronDown className="w-4 h-4 text-[#5A6C7D] shrink-0" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="start"
          className="w-64 bg-[#1A2B42] border-[#0A1628]"
          data-testid="project-switcher-menu"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          {projects.map(project => (
            <div key={project.id} className="flex items-center group">
              <DropdownMenuItem
                onClick={() => handleSwitch(project.id)}
                className="text-white hover:bg-[#0A1628] cursor-pointer flex-1 flex items-center justify-between"
                data-testid={`project-item-${project.id}`}
              >
                <span className="truncate">{project.name}</span>
                {project.id === activeProjectId && (
                  <Check className="w-4 h-4 text-[#00D9C8] shrink-0 ml-2" />
                )}
              </DropdownMenuItem>

              {/* Rename — verfügbar für ALLE Projekte (inkl. Standardprojekt) */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  openRename(project);
                }}
                title="Umbenennen"
                className="p-1.5 text-[#5A6C7D] hover:text-[#00D9C8] rounded opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`project-rename-${project.id}`}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>

              {/* Delete — verfügbar für alle Projekte AUSSER dem aktuell
                  aktiven (sonst wäre der UI-State broken). Backend legt
                  automatisch ein neues Default an, falls das letzte
                  Projekt entfernt wird. */}
              {project.id !== activeProjectId && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setDeleteTarget(project);
                  }}
                  title="Löschen"
                  className="p-1.5 mr-1 text-[#5A6C7D] hover:text-red-400 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`project-delete-${project.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}

          <DropdownMenuSeparator className="bg-[#0A1628]" />

          <DropdownMenuItem
            onClick={() => setShowOnboarding(true)}
            className="text-[#00D9C8] hover:bg-[#0A1628] cursor-pointer"
            data-testid="new-project-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {lang === 'en' ? 'New Project' : 'Neues Projekt'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#1A2B42] border-[#0A1628]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Projekt löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A0AEC0]">
              Dieses Projekt und alle zugehörigen Inhalte werden unwiderruflich gelöscht. Fortfahren?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#0A1628] text-white border-[#1A2B42] hover:bg-[#1A2B42]">
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-btn"
            >
              {deleting ? 'Löschen...' : 'Endgültig löschen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rename Dialog */}
      <Dialog open={!!renameTarget} onOpenChange={(open) => !open && setRenameTarget(null)}>
        <DialogContent className="bg-[#1A2B42] border-[#0A1628]" data-testid="project-rename-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Projekt umbenennen</DialogTitle>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !renaming) {
                e.preventDefault();
                handleRename();
              }
            }}
            placeholder="Projektname"
            className="bg-[#0A1628] text-white border-[#0A1628] focus-visible:ring-[#00D9C8]"
            autoFocus
            data-testid="project-rename-input"
          />
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setRenameTarget(null)}
              className="text-white hover:bg-[#0A1628]"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleRename}
              disabled={renaming || !renameValue.trim()}
              className="bg-[#00D9C8] hover:bg-[#00B8A8] text-[#0A1628]"
              data-testid="project-rename-confirm"
            >
              {renaming ? 'Speichern...' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showOnboarding && (
        <NewProjectOnboarding
          onComplete={handleOnboardingComplete}
          onCancel={() => setShowOnboarding(false)}
        />
      )}

      <UpgradeGatePopup
        open={showBrandGate}
        onOpenChange={setShowBrandGate}
        featureTitle="Mehrere Brand-Projekte"
        featureDescription="Du hast dein Brand-Limit im aktuellen Paket erreicht. Upgrade, um weitere Brand-Projekte parallel zu verwalten."
        requiredPlan="growth"
      />
    </>
  );
};
